var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, forwardRef, useCallback } from 'react';
import 'intersection-observer';
import { useInView } from 'react-intersection-observer';
var isSsr = typeof window === 'undefined';
var universalBtoa = isSsr
    ? function (str) { return Buffer.from(str.toString(), 'binary').toString('base64'); }
    : window.btoa;
var isIntersectionObserverAvailable = isSsr
    ? false
    : !!window.IntersectionObserver;
var imageAddStrategy = function (_a) {
    var lazyLoad = _a.lazyLoad, inView = _a.inView, loaded = _a.loaded;
    if (!lazyLoad) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return inView || loaded;
    }
    return true;
};
var imageShowStrategy = function (_a) {
    var lazyLoad = _a.lazyLoad, loaded = _a.loaded;
    if (!lazyLoad) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return loaded;
    }
    return true;
};
export var Image = forwardRef(function (_a, ref) {
    var _b;
    var className = _a.className, _c = _a.fadeInDuration, fadeInDuration = _c === void 0 ? 500 : _c, intersectionTreshold = _a.intersectionTreshold, intersectionThreshold = _a.intersectionThreshold, intersectionMargin = _a.intersectionMargin, pictureClassName = _a.pictureClassName, _d = _a.lazyLoad, lazyLoad = _d === void 0 ? true : _d, style = _a.style, pictureStyle = _a.pictureStyle, _e = _a.layout, layout = _e === void 0 ? 'intrinsic' : _e, objectFit = _a.objectFit, objectPosition = _a.objectPosition, data = _a.data, onLoad = _a.onLoad, _f = _a.usePlaceholder, usePlaceholder = _f === void 0 ? true : _f;
    var _g = useState(false), loaded = _g[0], setLoaded = _g[1];
    var handleLoad = function () {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
        setLoaded(true);
    };
    var _h = useInView({
        threshold: intersectionThreshold || intersectionTreshold || 0,
        rootMargin: intersectionMargin || '0px 0px 0px 0px',
        triggerOnce: true
    }), viewRef = _h[0], inView = _h[1];
    var callbackRef = useCallback(function (_ref) {
        viewRef(_ref);
        if (ref)
            ref.current = _ref;
    }, [viewRef]);
    var absolutePositioning = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%'
    };
    var addImage = imageAddStrategy({
        lazyLoad: lazyLoad,
        inView: inView,
        loaded: loaded
    });
    var showImage = imageShowStrategy({
        lazyLoad: lazyLoad,
        inView: inView,
        loaded: loaded
    });
    var webpSource = data.webpSrcSet && (React.createElement("source", { srcSet: data.webpSrcSet, sizes: data.sizes, type: "image/webp" }));
    var regularSource = data.srcSet && (React.createElement("source", { srcSet: data.srcSet, sizes: data.sizes }));
    var transition = fadeInDuration > 0 ? "opacity " + fadeInDuration + "ms" : undefined;
    var placeholder = usePlaceholder ? (React.createElement("img", { role: "presentation", src: data.base64, style: __assign({ backgroundColor: data.bgColor, opacity: showImage ? 0 : 1, transition: transition,
            objectFit: objectFit,
            objectPosition: objectPosition }, absolutePositioning) })) : null;
    var width = data.width, aspectRatio = data.aspectRatio;
    var height = data.height || width / aspectRatio;
    var svg = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"" + width + "\" height=\"" + height + "\"></svg>";
    var sizer = layout !== 'fill' ? (React.createElement("img", { className: pictureClassName, style: {
            display: 'block',
            width: '100%'
        }, src: "data:image/svg+xml;base64," + universalBtoa(svg), role: "presentation" })) : null;
    return (React.createElement("div", { ref: callbackRef, className: className, style: __assign(__assign({ overflow: 'hidden' }, (layout === 'fill'
            ? absolutePositioning
            : layout === 'intrinsic'
                ? { position: 'relative', width: '100%', maxWidth: width }
                : layout === 'fixed'
                    ? { position: 'relative', width: width }
                    : { position: 'relative', width: '100%' })), style) },
        sizer,
        placeholder,
        addImage && (React.createElement("picture", null,
            webpSource,
            regularSource,
            data.src && (React.createElement("img", { src: data.src, alt: data.alt, title: data.title, onLoad: handleLoad, className: pictureClassName, style: __assign(__assign(__assign({ opacity: showImage ? 1 : 0, transition: transition }, absolutePositioning), { objectFit: objectFit,
                    objectPosition: objectPosition }), pictureStyle) })))),
        React.createElement("noscript", null,
            React.createElement("picture", null,
                webpSource,
                regularSource,
                data.src && (React.createElement("img", { src: data.src, alt: (_b = data.alt) !== null && _b !== void 0 ? _b : '', title: data.title, className: pictureClassName, style: __assign(__assign({}, absolutePositioning), pictureStyle), loading: "lazy" }))))));
});
//# sourceMappingURL=index.js.map